import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../components/shared/Navbar';
import API from '../utils/api';
import SEO from '../components/SEO';
import { trackSearch } from '../utils/analytics';

const LANG_FLAGS = { en:'🇬🇧', es:'🇪🇸', fr:'🇫🇷', hi:'🇮🇳', gu:'🇮🇳', de:'🇩🇪', pt:'🇧🇷', ar:'🇸🇦', zh:'🇨🇳', ja:'🇯🇵', it:'🇮🇹' };
const GENRE_ICONS = {
  magical:'✨', adventure:'🗺️', adventurous:'🗺️', funny:'😄', calm:'🌙',
  educational:'📚', cozy:'🔥', exciting:'⚡', spooky:'👻', fantasy:'🧙',
  mystery:'🔍', nature:'🌿', science:'🔬', bedtime:'🛌',
};

const GENRES = ['magical','adventure','funny','calm','educational','cozy','exciting','spooky','fantasy','mystery'];
const LANGUAGES = [
  { code:'en', label:'English',    flag:'🇬🇧' }, { code:'es', label:'Spanish',    flag:'🇪🇸' },
  { code:'fr', label:'French',     flag:'🇫🇷' }, { code:'hi', label:'Hindi',       flag:'🇮🇳' },
  { code:'gu', label:'Gujarati',   flag:'🇮🇳' }, { code:'de', label:'German',      flag:'🇩🇪' },
  { code:'pt', label:'Portuguese', flag:'🇧🇷' }, { code:'ar', label:'Arabic',      flag:'🇸🇦' },
  { code:'zh', label:'Chinese',    flag:'🇨🇳' }, { code:'ja', label:'Japanese',    flag:'🇯🇵' },
  { code:'it', label:'Italian',    flag:'🇮🇹' },
];

const CommunityPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stories, setStories]     = useState([]);
  const [total,   setTotal]       = useState(0);
  const [pages,   setPages]       = useState(1);
  const [loading, setLoading]     = useState(true);
  const [stats,   setStats]       = useState(null);
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');

  const page     = parseInt(searchParams.get('page') || '1');
  const genre    = searchParams.get('genre')    || '';
  const language = searchParams.get('language') || '';
  const sort     = searchParams.get('sort')     || 'featured';
  const search   = searchParams.get('search')   || '';

  const set = (key, val) => {
    const p = Object.fromEntries(searchParams);
    if (val) p[key] = val; else delete p[key];
    if (key !== 'page') p.page = '1';
    setSearchParams(p);
  };

  // Only the newest request may write state. `load` is rebuilt whenever any of
  // the five filters changes, so the effect refires on every genre, language,
  // sort, search or page change — the most filter-dense screen in the app, and
  // public-facing. Without this an earlier response could land last and repaint
  // the grid with results for a filter the user had already moved off, while the
  // controls showed the newer one. The route key cannot help here: the pathname
  // never changes.
  const requestIdRef = useRef(0);

  const load = useCallback(() => {
    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;

    setLoading(true);
    const params = { page, limit: 12, sort };
    if (genre)    params.genre    = genre;
    if (language) params.language = language;
    if (search)   params.search   = search;
    API.get('/stories/community', { params })
      .then(r => {
        if (isStale()) return;
        setStories(r.data.stories); setTotal(r.data.total); setPages(r.data.pages);
      })
      .catch(console.error)
      .finally(() => { if (!isStale()) setLoading(false); });
  }, [page, genre, language, sort, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    API.get('/stories/community/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
      <SEO
        title="Story Community — Discover Personalised Bedtime Stories"
        description="Browse hundreds of personalised bedtime stories shared by families around the world. Find magical tales by age, genre, and language."
        canonical="/community"
      />
      <Navbar />

      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg,#2d1b69 0%,#6f42c1 100%)', padding: '64px 0 48px' }}>
        <div className="container text-center">
          <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill" style={{ fontSize: '13px' }}>
            🌍 Community Library
          </span>
          <h1 className="fw-bold text-white mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(28px,5vw,48px)' }}>
            Stories shared by families like yours
          </h1>
          <p className="mb-4" style={{ color: '#c9a8ff', fontSize: '17px', maxWidth: '560px', margin: '0 auto 24px' }}>
            Parents from around the world have published their children's favourite personalised stories. Browse, get inspired, or share your own.
          </p>

          {/* Stats */}
          {stats && (
            <div className="d-flex justify-content-center gap-4 flex-wrap mb-4">
              {[
                { value: stats.totalPublished, label: 'Stories published' },
                { value: stats.totalViews.toLocaleString(), label: 'Total reads' },
                { value: stats.totalLikes.toLocaleString(), label: 'Hearts given' },
              ].map(s => (
                <div key={s.label} className="text-center">
                  <div className="fw-bold text-white" style={{ fontSize: '28px', lineHeight: 1 }}>{s.value}</div>
                  <div style={{ color: '#c9a8ff', fontSize: '13px' }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Search bar */}
          <form onSubmit={e => { e.preventDefault(); if (searchInput.trim()) trackSearch(searchInput.trim(), 'community'); set('search', searchInput); }}
            className="d-flex gap-2 justify-content-center" style={{ maxWidth: '480px', margin: '0 auto' }}>
            <input
              className="form-control rounded-pill"
              placeholder="Search stories…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              style={{ fontSize: '15px', paddingLeft: '20px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
            />
            <button type="submit" className="btn btn-warning rounded-pill px-4 fw-semibold flex-shrink-0">
              <i className="bi bi-search me-1"></i>Search
            </button>
          </form>
        </div>
      </div>

      <div className="container py-5">

        {/* Filter bar */}
        <div className="card border-0 shadow-sm mb-5 p-3" style={{ borderRadius: '16px' }}>
          <div className="d-flex flex-wrap gap-3 align-items-center">

            {/* Sort */}
            <div className="d-flex gap-2">
              {[
                { value: 'featured', icon: '⭐', label: 'Featured' },
                { value: 'newest',   icon: '🆕', label: 'Newest'   },
                { value: 'popular',  icon: '🔥', label: 'Popular'  },
                { value: 'reading',  icon: '⚡', label: 'Quick read' },
              ].map(s => (
                <button key={s.value} type="button"
                  className={`btn btn-sm rounded-pill ${sort === s.value ? 'btn-dream' : 'btn-outline-secondary'}`}
                  onClick={() => set('sort', s.value)}>
                  {s.icon} {s.label}
                </button>
              ))}
            </div>

            {/* Genre filter */}
            <select className="form-select w-auto" value={genre} onChange={e => set('genre', e.target.value)}
              style={{ borderRadius: '12px', fontSize: '14px' }}>
              <option value="">All genres</option>
              {GENRES.map(g => (
                <option key={g} value={g}>{GENRE_ICONS[g] || '📖'} {g.charAt(0).toUpperCase() + g.slice(1)}</option>
              ))}
            </select>

            {/* Language filter */}
            <select className="form-select w-auto" value={language} onChange={e => set('language', e.target.value)}
              style={{ borderRadius: '12px', fontSize: '14px' }}>
              <option value="">All languages</option>
              {LANGUAGES.map(l => (
                <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
              ))}
            </select>

            {(genre || language || search) && (
              <button className="btn btn-sm btn-outline-secondary rounded-pill ms-auto"
                onClick={() => setSearchParams({ sort, page: '1' })}>
                <i className="bi bi-x me-1"></i>Clear filters
              </button>
            )}

            <span className="text-muted small ms-auto">{total.toLocaleString()} stories</span>
          </div>
        </div>

        {/* Active search badge */}
        {search && (
          <div className="mb-4">
            <span className="badge rounded-pill px-3 py-2" style={{ background: '#e8d5ff', color: '#6f42c1', fontSize: '13px' }}>
              <i className="bi bi-search me-1"></i>Results for: "{search}"
              <button type="button" className="btn-close ms-2" style={{ fontSize: '9px' }}
                onClick={() => { setSearchInput(''); setSearchParams({ sort }); }}></button>
            </span>
          </div>
        )}

        {/* Stories grid */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: '#6f42c1', width: '3rem', height: '3rem' }}></div>
            <p className="text-muted mt-3">Loading stories…</p>
          </div>
        ) : stories.length === 0 ? (
          <div className="text-center py-5">
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>📚</div>
            <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>No stories found</h5>
            <p className="text-muted mb-4">
              {search ? `No stories match "${search}".` : 'Be the first to publish a story!'}
            </p>
            <Link to="/register" className="btn btn-dream rounded-pill px-5">Get Started Free</Link>
          </div>
        ) : (
          <>
            <div className="row g-4">
              {stories.map(story => (
                <div className="col-md-6 col-lg-4" key={story._id || story.publishSlug}>
                  <Link to={`/community/${story.publishSlug}`} className="text-decoration-none">
                    <div className="card border-0 shadow-sm h-100 card-hover" style={{ borderRadius: '20px', overflow: 'hidden', transition: 'transform 0.15s', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'none'}>

                      {/* Featured ribbon */}
                      {story.isFeatured && (
                        <div style={{ position: 'absolute', top: '12px', left: '12px', zIndex: 2 }}>
                          <span className="badge rounded-pill px-2 py-1" style={{ background: 'linear-gradient(135deg,#f59e0b,#fbbf24)', color: 'white', fontSize: '11px' }}>
                            ⭐ Featured
                          </span>
                        </div>
                      )}

                      {/* Illustration */}
                      {story.illustrationUrl
                        ? <img src={story.illustrationUrl} alt={story.title}
                            style={{ width: '100%', height: '170px', objectFit: 'cover', display: 'block' }} />
                        : <div className="d-flex align-items-center justify-content-center"
                            style={{ height: '100px', background: 'linear-gradient(135deg,#667eea,#764ba2)' }}>
                            <span style={{ fontSize: '40px' }}>🌙</span>
                          </div>
                      }

                      <div className="card-body p-4">
                        <h6 className="fw-bold mb-2" style={{ color: 'var(--text-main)', lineHeight: '1.4', fontSize: '15px' }}>
                          {story.title}
                        </h6>

                        {/* Child name + age group */}
                        <div className="text-muted small mb-3" style={{ fontSize: '12px' }}>
                          <i className="bi bi-person-heart me-1" style={{ color: '#9b6fe8' }}></i>
                          {story.publishedChildName}
                          {story.ageGroup && <span className="ms-2">· ages {story.ageGroup}</span>}
                        </div>

                        {/* Moral lesson preview */}
                        {story.moralLesson && (
                          <p className="text-muted mb-3" style={{ fontSize: '12px', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            💫 {story.moralLesson}
                          </p>
                        )}

                        {/* Badges */}
                        <div className="d-flex flex-wrap gap-1 mb-3">
                          {story.genre && (
                            <span className="badge rounded-pill" style={{ background: '#e8d5ff', color: '#6f42c1', fontSize: '10px' }}>
                              {GENRE_ICONS[story.genre] || '📖'} {story.genre.charAt(0).toUpperCase() + story.genre.slice(1)}
                            </span>
                          )}
                          {story.language && story.language !== 'en' && (
                            <span className="badge rounded-pill" style={{ background: '#fff3cd', color: '#856404', fontSize: '10px' }}>
                              {LANG_FLAGS[story.language] || '🌐'} {story.language.toUpperCase()}
                            </span>
                          )}
                          {story.learningTopic && (
                            <span className="badge rounded-pill" style={{ background: '#dcfce7', color: '#15803d', fontSize: '10px' }}>
                              🎓 {story.learningTopic}
                            </span>
                          )}
                          {story.isMultiChild && (
                            <span className="badge rounded-pill" style={{ background: '#e8d5ff', color: '#6f42c1', fontSize: '10px' }}>
                              👨‍👩‍👧‍👦 Family
                            </span>
                          )}
                        </div>

                        {/* Footer stats */}
                        <div className="d-flex align-items-center justify-content-between" style={{ fontSize: '11px', color: '#9ca3af' }}>
                          <span>
                            <i className="bi bi-clock me-1"></i>{story.readingTime || 5} min
                          </span>
                          <div className="d-flex gap-3">
                            <span><i className="bi bi-eye me-1"></i>{story.communityViews || 0}</span>
                            <span><i className="bi bi-heart-fill me-1" style={{ color: '#f87171' }}></i>{story.communityLikes || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="d-flex justify-content-center gap-2 mt-5">
                <button className="btn btn-outline-secondary rounded-pill" disabled={page <= 1}
                  onClick={() => set('page', String(page - 1))}>
                  <i className="bi bi-chevron-left"></i>
                </button>
                {[...Array(Math.min(pages, 7))].map((_, i) => {
                  const p = i + 1;
                  return (
                    <button key={p} onClick={() => set('page', String(p))}
                      className={`btn rounded-pill ${page === p ? 'btn-dream' : 'btn-outline-secondary'}`}>
                      {p}
                    </button>
                  );
                })}
                {pages > 7 && <span className="btn btn-sm disabled">…{pages}</span>}
                <button className="btn btn-outline-secondary rounded-pill" disabled={page >= pages}
                  onClick={() => set('page', String(page + 1))}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}

        {/* CTA for non-users */}
        <div className="card border-0 shadow-sm mt-5 p-5 text-center" style={{ borderRadius: '24px', background: 'linear-gradient(135deg,#2d1b69,#6f42c1)' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🌙</div>
          <h4 className="fw-bold text-white mb-2" style={{ fontFamily: "'Fredoka One',cursive" }}>
            Generate your own magical stories
          </h4>
          <p style={{ color: '#c9a8ff', fontSize: '16px', maxWidth: '480px', margin: '0 auto 24px' }}>
            Join thousands of families creating personalised bedtime stories. Free to start — 5 stories every month.
          </p>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <Link to="/register" className="btn btn-warning btn-lg rounded-pill fw-bold px-5">
              Start Free
            </Link>
            <Link to="/pricing" className="btn btn-outline-light btn-lg rounded-pill px-4">
              See Pricing
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityPage;
